import React, { Component } from "react";
import {
  Select,
  Button,
  message,
  List,
  Card,
  Tag,
  Popconfirm,
  InputNumber,
} from "antd";
import { PlusOutlined, DeleteOutlined, UserOutlined } from "@ant-design/icons";
import axios from "axios";
import { JS_COOKIE } from "util/auth";
import { ADMIN_COOKIE_NAME } from "constants/admin";
import api_address from "constants/config";
import { client } from "graphql/ApolloClient";
import { ADD_COACH_TO_CHILD } from "graphql/mutations/addCoachToChild";
import { REMOVE_COACH_FROM_CHILD } from "graphql/mutations/removeCoachFromChild";

const { Option } = Select;

interface CoachAssignmentProps {
  resultId: number;
  clientUuid?: string;
  childId?: string;
  therapistAssigned?: boolean;
}

interface CoachAssignmentState {
  availableCoaches: Array<{
    id: number;
    uuid: string;
    firstName: string;
    lastName: string;
    email: string;
  }>;
  assignedCoaches: Array<{
    id: number;
    uuid: string;
    firstName: string;
    lastName: string;
    email: string;
    assigned_at: string;
  }>;
  selectedCoachId: number | null;
  additionalCoachingSessions: number;
  loading: boolean;
  assigning: boolean;
}

class CoachAssignment extends Component<
  CoachAssignmentProps,
  CoachAssignmentState
> {
  constructor(props: CoachAssignmentProps) {
    super(props);
    this.state = {
      availableCoaches: [],
      assignedCoaches: [],
      selectedCoachId: null,
      additionalCoachingSessions: 0,
      loading: false,
      assigning: false,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    this.setState({ loading: true });
    try {
      await Promise.all([
        this.loadAvailableCoaches(),
        this.loadAssignedCoaches(),
      ]);
    } catch (error) {
      console.error("Error loading data:", error);
      message.error("Failed to load coach data");
    } finally {
      this.setState({ loading: false });
    }
  };

  loadAvailableCoaches = async () => {
    try {
      const { data } = await axios.get(api_address + "api/admin/coach", {
        headers: {
          Authorization: `Bearer ${JS_COOKIE.get(ADMIN_COOKIE_NAME)}`,
        },
      });
      this.setState({
        availableCoaches: data.coach,
      });
    } catch (error) {
      console.error("Error loading available coaches:", error);
    }
  };

  loadAssignedCoaches = async () => {
    try {
      const response = await axios.get(
        `${api_address}api/admin/result/${this.props.resultId}/coaches`,
        {
          headers: {
            Authorization: `Bearer ${JS_COOKIE.get(ADMIN_COOKIE_NAME)}`,
          },
        }
      );
      this.setState({
        assignedCoaches: response.data.coaches || [],
      });
    } catch (error) {
      console.error("Error loading assigned coaches:", error);
    }
  };

  handleAssignCoach = async () => {
    const { selectedCoachId, additionalCoachingSessions, assignedCoaches } =
      this.state;
    const { resultId, clientUuid, therapistAssigned } = this.props;

    if (!therapistAssigned) {
      message.warning(
        "Please assign a therapist first before assigning coaches"
      );
      return;
    }

    if (!selectedCoachId) {
      message.warning("Please select a coach to assign");
      return;
    }

    this.setState({ assigning: true });
    try {
      const isFirstCoach = assignedCoaches.length === 0;
      const additionalSessionsForAPI = isFirstCoach
        ? additionalCoachingSessions
        : 0;

      await axios.post(
        `${api_address}api/admin/result/assignCoach`,
        {
          coachId: selectedCoachId,
          resultId: resultId,
          additionalCoachingSessions: additionalSessionsForAPI,
        },
        {
          headers: {
            Authorization: `Bearer ${JS_COOKIE.get(ADMIN_COOKIE_NAME)}`,
          },
        }
      );

      // If clientUuid is provided, also call the GraphQL mutation to update guide DB
      if (clientUuid) {
        // Find the selected coach's UUID
        const selectedCoach = this.state.availableCoaches.find(
          (coach) => coach.id === selectedCoachId
        );
        if (selectedCoach) {
          try {
            await client.mutate({
              mutation: ADD_COACH_TO_CHILD,
              variables: {
                coachUuid: selectedCoach.uuid,
                clientUuid: clientUuid,
                additionalCoachingSessions: additionalSessionsForAPI,
              },
            });
            message.success("Coach-child relationship added to guide database");
          } catch (guideError) {
            message.error("Failed to update guide database");
            console.error("Error updating guide database:", guideError);
          }
        }
      }

      message.success("Coach assigned successfully on eval");
      this.setState({ selectedCoachId: null, additionalCoachingSessions: 0 });
      await this.loadAssignedCoaches();
    } catch (error: any) {
      console.error("Error assigning coach:", error);
      const errorMessage =
        error.response?.data?.data?.message || "Failed to assign coach";
      message.error(errorMessage);
    } finally {
      this.setState({ assigning: false });
    }
  };

  handleRemoveCoach = async (coachId: number) => {
    const { resultId, clientUuid, therapistAssigned } = this.props;

    if (!therapistAssigned) {
      message.warning(
        "Please assign a therapist first before managing coach assignments"
      );
      return;
    }

    try {
      // First, remove coach assignment from the eval API
      await axios.delete(`${api_address}api/admin/result/assignCoach`, {
        headers: {
          Authorization: `Bearer ${JS_COOKIE.get(ADMIN_COOKIE_NAME)}`,
        },
        data: {
          coachId: coachId,
          resultId: resultId,
        },
      });

      // If clientUuid is provided, also call the GraphQL mutation to update guide DB
      if (clientUuid) {
        // Find the coach's UUID from assigned coaches
        const coachToRemove = this.state.assignedCoaches.find(
          (coach) => coach.id === coachId
        );
        if (coachToRemove) {
          try {
            await client.mutate({
              mutation: REMOVE_COACH_FROM_CHILD,
              variables: {
                coachUuid: coachToRemove.uuid,
                clientUuid: clientUuid,
              },
            });
            message.success(
              "Coach-child relationship removed from guide database"
            );
          } catch (guideError) {
            message.error("Failed to update guide database");
            console.error("Error updating guide database:", guideError);
            // Don't fail the whole operation if guide update fails
          }
        }
      }

      message.success("Coach assignment removed from eval");
      await this.loadAssignedCoaches();
    } catch (error: any) {
      console.error("Error removing coach assignment:", error);
      const errorMessage =
        error.response?.data?.data?.message ||
        "Failed to remove coach assignment";
      message.error(errorMessage);
    }
  };

  render() {
    const {
      availableCoaches,
      assignedCoaches,
      selectedCoachId,
      additionalCoachingSessions,
      loading,
      assigning,
    } = this.state;
    const { therapistAssigned } = this.props;

    // Filter out already assigned coaches from the available list
    const assignedCoachIds = assignedCoaches.map((coach) => coach.id);
    const unassignedCoaches = availableCoaches.filter(
      (coach) => !assignedCoachIds.includes(coach.id)
    );

    return (
      <Card
        style={{ border: "0.5px solid #6898e3", fontSize: 16 }}
        loading={loading}
      >
        {!therapistAssigned && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: "#fff7e6",
              border: "1px solid #ffd591",
              borderRadius: 6,
              color: "#d46b08",
            }}
          >
            <strong>
              ⚠️ Please assign a therapist first before assigning coaches
            </strong>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <Select
              style={{ flex: 1, minWidth: 200 }}
              placeholder={
                therapistAssigned
                  ? "Select a coach to assign"
                  : "Please assign a therapist first"
              }
              value={selectedCoachId}
              onChange={(value) => this.setState({ selectedCoachId: value })}
              disabled={!therapistAssigned}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase()) || false
              }
            >
              {unassignedCoaches.map((coach) => (
                <Option key={coach.id} value={coach.id}>
                  {coach.firstName} {coach.lastName} ({coach.email})
                </Option>
              ))}
            </Select>
            {assignedCoaches.length === 0 && (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <label style={{ margin: 0, whiteSpace: "nowrap" }}>
                  Additional Sessions:
                </label>
                <InputNumber
                  min={0}
                  value={additionalCoachingSessions}
                  onChange={(value) =>
                    this.setState({
                      additionalCoachingSessions: value || 0,
                    })
                  }
                  disabled={!therapistAssigned}
                  style={{ width: 80 }}
                />
              </div>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined rev={undefined} />}
              onClick={this.handleAssignCoach}
              loading={assigning}
              disabled={!selectedCoachId || !therapistAssigned}
              title={
                !therapistAssigned ? "Please assign a therapist first" : ""
              }
            >
              Assign
            </Button>
          </div>
        </div>

        <List
          dataSource={assignedCoaches}
          locale={{
            emptyText: !therapistAssigned
              ? "Please assign a therapist first to manage coach assignments"
              : "No coaches assigned to this video",
          }}
          renderItem={(coach) => (
            <List.Item
              actions={[
                <Popconfirm
                  title={
                    !therapistAssigned
                      ? "Please assign a therapist first"
                      : "Are you sure you want to remove this coach assignment?"
                  }
                  onConfirm={() => this.handleRemoveCoach(coach.id)}
                  okText="Yes"
                  cancelText="No"
                  disabled={!therapistAssigned}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined rev={undefined} />}
                    disabled={!therapistAssigned}
                    title={
                      !therapistAssigned
                        ? "Please assign a therapist first"
                        : ""
                    }
                  >
                    Remove
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <UserOutlined
                    rev={undefined}
                    style={{ fontSize: 24, color: "#1890ff" }}
                  />
                }
                title={`${coach.firstName} ${coach.lastName}`}
                description={
                  <div>
                    <div>{coach.email}</div>
                    <Tag>
                      Assigned:{" "}
                      {new Date(coach.assigned_at).toLocaleDateString()}
                    </Tag>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>
    );
  }
}

export default CoachAssignment;
